import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import {
  Buildings, Plus, MagnifyingGlass, Trash, Pause, Play,
  ArrowClockwise, Lightning, ArrowSquareOut, CaretDown, CaretUp,
  CheckCircle, Warning, Clock, Crosshair
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function WatchlistPanel() {
  const {
    loading, activePersonaId,
    fetchWatchlist, addWatchlistCompany, removeWatchlistCompany,
    toggleWatchlistPause, scanWatchlistCompany, fetchWatchlistJobs,
    fetchAutoApplyCandidates, scanAllWatchlist, fetchAutomationStatus, setSelectedJob,
  } = useApp();

  const [companies, setCompanies] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [companyJobs, setCompanyJobs] = useState({});
  const [autoApply, setAutoApply] = useState([]);
  const [automationInfo, setAutomationInfo] = useState(null);

  const loadWatchlist = useCallback(async () => {
    const data = await fetchWatchlist();
    setCompanies(data.companies || []);
  }, [fetchWatchlist]);

  const loadAutoApply = useCallback(async () => {
    const data = await fetchAutoApplyCandidates();
    setAutoApply(data.candidates || []);
  }, [fetchAutoApplyCandidates]);

  useEffect(() => {
    loadWatchlist();
    loadAutoApply();
    fetchAutomationStatus().then(s => s && setAutomationInfo(s));
  }, [loadWatchlist, loadAutoApply, fetchAutomationStatus]);

  const handleScanAll = async () => {
    toast.info("Scanning all companies in background...");
    const result = await scanAllWatchlist();
    if (result?.error) toast.error(result.error);
    else toast.success(`Scan triggered for ${result.companies_to_scan} companies`);
  };

  const handleAdd = async () => {
    if (!nameInput.trim()) { toast.error("Company name is required"); return; }
    const result = await addWatchlistCompany({
      name: nameInput.trim(),
      career_url: urlInput.trim(),
      persona_id: activePersonaId || "",
    });
    if (result?.error) { toast.error(result.error); return; }
    toast.success(`${nameInput} added to watchlist`);
    setNameInput(""); setUrlInput(""); setShowAdd(false);
    loadWatchlist();
  };

  const handleRemove = async (id, name) => {
    await removeWatchlistCompany(id);
    toast.success(`${name} removed`);
    loadWatchlist();
  };

  const handleTogglePause = async (id) => {
    const updated = await toggleWatchlistPause(id);
    if (updated) {
      toast.success(`${updated.name} ${updated.status === "paused" ? "paused" : "resumed"}`);
      loadWatchlist();
    }
  };

  const handleScan = async (id, name) => {
    toast.info(`Scanning ${name}...`);
    const result = await scanWatchlistCompany(id);
    if (result?.error) {
      toast.error(result.error);
    } else {
      toast.success(`${name}: ${result.jobs_found} jobs found, ${result.jobs_stored} new`);
      if (result.auto_apply_candidates > 0) {
        toast.success(`${result.auto_apply_candidates} auto-apply candidates!`, { icon: <Lightning size={16} weight="fill" /> });
      }
    }
    loadWatchlist();
    loadAutoApply();
  };

  const toggleExpand = async (companyId) => {
    if (expandedId === companyId) { setExpandedId(null); return; }
    setExpandedId(companyId);
    if (!companyJobs[companyId]) {
      const data = await fetchWatchlistJobs(companyId);
      setCompanyJobs(prev => ({ ...prev, [companyId]: data.jobs || [] }));
    }
  };

  const statusIcon = (status) => {
    if (status === "active") return <CheckCircle size={14} weight="fill" className="text-emerald-500" />;
    if (status === "paused") return <Pause size={14} weight="fill" className="text-zinc-500" />;
    return <Warning size={14} weight="fill" className="text-red-500" />;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="watchlist-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight flex items-center gap-3" data-testid="watchlist-title">
            <Buildings size={28} weight="fill" className="text-amber-500" />
            Target Companies
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Track companies and auto-discover jobs from their career pages</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleScanAll}
            disabled={loading.scanAll}
            variant="outline"
            className="border-white/10 text-zinc-400 hover:text-white gap-2"
            data-testid="scan-all-btn"
          >
            <ArrowClockwise size={14} className={loading.scanAll ? "animate-spin" : ""} />
            {loading.scanAll ? "Scanning..." : "Scan All"}
          </Button>
          <Button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold gap-2"
            data-testid="add-company-btn"
          >
            <Plus size={16} weight="bold" /> Add Company
          </Button>
        </div>
      </div>

      {/* Automation Status Banner */}
      {automationInfo && (
        <div className="flex items-center gap-4 px-4 py-2.5 rounded-lg border border-white/5 bg-white/[0.02]" data-testid="automation-banner">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${automationInfo.watchlist?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
            <span className="text-[11px] text-zinc-500">Watchlist Cron: {automationInfo.watchlist?.enabled ? `every ${automationInfo.watchlist.interval_hours}h` : 'OFF'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${automationInfo.radar?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
            <span className="text-[11px] text-zinc-500">Radar: {automationInfo.radar?.enabled ? `every ${automationInfo.radar.interval_hours}h` : 'OFF'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${automationInfo.auto_apply?.enabled ? 'bg-amber-500' : 'bg-zinc-600'}`} />
            <span className="text-[11px] text-zinc-500">Auto-Apply: {automationInfo.auto_apply?.enabled ? `${automationInfo.auto_apply.min_score}%+` : 'OFF'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[11px] text-zinc-500">Auto-Score: ON</span>
          </div>
        </div>
      )}

      {/* Add Company Form */}
      {showAdd && (
        <div className="jp-card p-5 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="add-company-form">
          <div className="jp-label mb-2">ADD NEW COMPANY</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Company Name *</label>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="e.g. OpenAI"
                data-testid="company-name-input"
                className="w-full bg-[#0a0a0f] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Career Page URL (optional)</label>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="e.g. https://openai.com/careers"
                data-testid="company-url-input"
                className="w-full bg-[#0a0a0f] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={loading.watchlist} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="submit-company-btn">
              {loading.watchlist ? "Adding..." : "Add to Watchlist"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowAdd(false); setNameInput(""); setUrlInput(""); }} className="text-zinc-500">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Auto-Apply Candidates */}
      {autoApply.length > 0 && (
        <div className="jp-card border-amber-500/30" data-testid="auto-apply-section">
          <div className="jp-card-header bg-amber-500/5">
            <div className="flex items-center gap-2">
              <Lightning size={18} weight="fill" className="text-amber-500" />
              <span className="font-['Outfit'] font-semibold text-white text-sm">Auto-Apply Candidates</span>
              <span className="bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full font-mono">{autoApply.length}</span>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-[240px] overflow-y-auto">
            {autoApply.map(job => (
              <div key={job.id} className="flex items-center gap-3 px-3 py-2.5 bg-[#0a0a0f] rounded-md border border-white/5 hover:border-amber-500/20 transition-colors group" data-testid={`auto-apply-job-${job.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{job.title}</div>
                  <div className="text-xs text-zinc-500">{job.company} {job.location && `- ${job.location}`}</div>
                </div>
                <div className="font-mono text-xs font-bold text-amber-400">{job.match_score}%</div>
                {job.job_url && (
                  <a
                    href={job.job_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`apply-link-${job.id}`}
                    className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-bold px-3 py-1.5 rounded-md transition-colors"
                  >
                    Apply <ArrowSquareOut size={12} weight="bold" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company List */}
      {companies.length === 0 && !showAdd ? (
        <div className="jp-card p-12 text-center" data-testid="empty-watchlist">
          <Buildings size={48} className="text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 text-sm mb-1">No companies in your watchlist yet</p>
          <p className="text-zinc-600 text-xs">Add companies to automatically track their job openings</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="company-list">
          {companies.map(company => (
            <div key={company.id} className="jp-card" data-testid={`company-card-${company.id}`}>
              {/* Company Row */}
              <div className="flex items-center gap-4 px-5 py-4">
                {/* Status + Name */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {statusIcon(company.status)}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate">{company.name}</div>
                    {company.career_url ? (
                      <a href={company.career_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-600 hover:text-amber-500 truncate block transition-colors">
                        {company.career_url.replace(/https?:\/\//, '').slice(0, 50)}
                      </a>
                    ) : (
                      <span className="text-[11px] text-zinc-700 italic">No URL - will auto-discover</span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-zinc-500 shrink-0">
                  {company.last_scanned && (
                    <div className="flex items-center gap-1" title={`Last scanned: ${new Date(company.last_scanned).toLocaleString()}`}>
                      <Clock size={12} />
                      <span>{new Date(company.last_scanned).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 font-mono">
                    <Crosshair size={12} className="text-amber-500" />
                    <span className="text-amber-400">{company.jobs_found}</span>
                    <span>jobs</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleScan(company.id, company.name)}
                    disabled={loading[`scan-${company.id}`]}
                    data-testid={`scan-btn-${company.id}`}
                    className="h-8 w-8 text-zinc-500 hover:text-amber-500"
                    title="Scan now"
                  >
                    <MagnifyingGlass size={15} className={loading[`scan-${company.id}`] ? "animate-spin" : ""} />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleTogglePause(company.id)}
                    data-testid={`pause-btn-${company.id}`}
                    className="h-8 w-8 text-zinc-500 hover:text-zinc-300"
                    title={company.status === "paused" ? "Resume" : "Pause"}
                  >
                    {company.status === "paused" ? <Play size={15} /> : <Pause size={15} />}
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleRemove(company.id, company.name)}
                    data-testid={`remove-btn-${company.id}`}
                    className="h-8 w-8 text-zinc-500 hover:text-red-400"
                    title="Remove"
                  >
                    <Trash size={15} />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => toggleExpand(company.id)}
                    data-testid={`expand-btn-${company.id}`}
                    className="h-8 w-8 text-zinc-500 hover:text-zinc-300"
                    title="View jobs"
                  >
                    {expandedId === company.id ? <CaretUp size={15} /> : <CaretDown size={15} />}
                  </Button>
                </div>
              </div>

              {/* Error Banner */}
              {company.status === "error" && company.error_msg && (
                <div className="px-5 py-2 bg-red-500/5 border-t border-red-500/10 text-xs text-red-400 flex items-center gap-2">
                  <Warning size={13} /> {company.error_msg}
                </div>
              )}

              {/* Expanded Jobs List */}
              {expandedId === company.id && (
                <div className="border-t border-white/5 bg-[#0a0a0f]/50 p-4 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`jobs-list-${company.id}`}>
                  {(companyJobs[company.id] || []).length === 0 ? (
                    <p className="text-xs text-zinc-600 text-center py-4">No jobs discovered yet. Click scan to search.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {(companyJobs[company.id] || []).map(job => (
                        <div
                          key={job.id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-white/5 hover:border-white/10 transition-colors cursor-pointer group"
                          onClick={() => setSelectedJob(job)}
                          data-testid={`watchlist-job-${job.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white group-hover:text-amber-400 transition-colors truncate">{job.title}</div>
                            <div className="text-[11px] text-zinc-600">{job.location || "Location N/A"}</div>
                          </div>
                          {job.scored && (
                            <div className={`font-mono text-xs font-bold ${job.match_score >= 80 ? 'text-emerald-400' : job.match_score >= 60 ? 'text-amber-400' : 'text-zinc-500'}`}>
                              {job.match_score}%
                            </div>
                          )}
                          {job.job_url && (
                            <a
                              href={job.job_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-zinc-600 hover:text-amber-500 transition-colors"
                            >
                              <ArrowSquareOut size={14} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
